import { useContactForm } from "#src/components/steps/contact/contact-form.composable.mjs";
import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";
import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import { useStepAddressStore } from "#src/stores/step-address.ts";
import { useStepQuoteStore } from "#src/stores/step-quote.ts";
import { type InsuredType } from "#src/types/Insured.ts";
import { type ApiFlowStep } from "#src/types/StepStore.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed } from "vue";

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    name: required(REQS.INSURED_NAME),
    sendClientIntroMessage: required(REQS.SEND_CLIENT_INTRO_MESSAGE),
  };
}

interface ContactState {
  requiredFields: Record<string, boolean>;
}

function initialState(): ContactState {
  return {
    requiredFields: initializeRequiredFields(),
  };
}

interface ContactStep extends ApiFlowStep, ToSetupState<ContactState> {
  setStepRequirements(srq: DataRequirements): void;
}

function initializeContactStep(pinia: Pinia): ContactStep {
  const state = generateInitialState<ContactState>(initialState());

  function $reset() {
    resetState<ContactState>(state, initialState());
  }

  const step = computed(() => STEP.CONTACT);
  const next = computed(() => {
    const address = useStepAddressStore(pinia);
    if (address.inactive) return address.next;
    return address.step;
  });
  const previous = computed(() => {
    const quote = useStepQuoteStore(pinia);
    return quote.step;
  });

  function generateForm(module: InsuredType, args: any) {
    return useContactForm(pinia, module, args);
  }
  function setStepRequirements(srq: DataRequirements) {
    state.requiredFields.value = initializeRequiredFields(srq);
  }

  return {
    $reset,
    ...state,
    step,
    next,
    previous,
    generateForm,
    setStepRequirements,
  };
}

export const useStepContactStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, ContactStep>("step-contact", () => {
    return initializeContactStep(pinia);
  })(pinia, hot);
};
